import { handleAsync } from "@/utils";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import type {
  TBackendPaginationMeta,
  TDailyLimitResetResponse,
  TEmailLogRecord,
  TAuditLogRecord,
  TLegalHoldResponse,
  TPagedMeta,
  TPhoneLogRecord,
  TVerificationConfigResponse,
  TVerificationConfigUpdate,
  TVerificationStatsResponse,
} from "./verification-admin.types";

const apiService = new ApiService({ hooks: { ...withAuthHooks } });

type TPagedResponse<T> = { data: T[]; meta: TPagedMeta | TBackendPaginationMeta };
type TWrap<T> = { data: T };

export type TEmailLogsParams = {
  user_id?: string;
  email?: string;
  delivery_status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TPhoneLogsParams = {
  user_id?: string;
  phone_number?: string;
  delivery_status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TAuditLogsParams = {
  user_id?: string;
  channel?: "email" | "phone";
  severity?: "INFO" | "WARNING" | "HIGH" | "CRITICAL";
  event?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== "") q.set(key, String(val));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

class VerificationAdminService {
  private base = "admin/verification" as const;

  // Email Logs
  listEmailLogs(params: TEmailLogsParams = {}, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TPagedResponse<TEmailLogRecord>>(() =>
      apiService.get(`${this.base}/email-logs${buildQuery(params as Record<string, string | number | undefined>)}`, options)
    );
  }

  getEmailLog(id: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TEmailLogRecord>>(() =>
      apiService.get(`${this.base}/email-logs/${id}`, options)
    );
  }

  // Phone Logs
  listPhoneLogs(params: TPhoneLogsParams = {}, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TPagedResponse<TPhoneLogRecord>>(() =>
      apiService.get(`${this.base}/phone-logs${buildQuery(params as Record<string, string | number | undefined>)}`, options)
    );
  }

  getPhoneLog(id: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TPhoneLogRecord>>(() =>
      apiService.get(`${this.base}/phone-logs/${id}`, options)
    );
  }

  // Audit Logs
  listAuditLogs(params: TAuditLogsParams = {}, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TPagedResponse<TAuditLogRecord>>(() =>
      apiService.get(`${this.base}/audit-logs${buildQuery(params as Record<string, string | number | undefined>)}`, options)
    );
  }

  // Stats
  getStats(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TVerificationStatsResponse>>(() =>
      apiService.get(`${this.base}/stats`, options)
    );
  }

  // Config
  getConfig(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TVerificationConfigResponse>>(() =>
      apiService.get(`${this.base}/config`, options)
    );
  }

  updateConfig(payload: TVerificationConfigUpdate, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TVerificationConfigResponse>>(() =>
      apiService.put(`${this.base}/config`, payload, options)
    );
  }

  // Daily Limit Reset
  resetDailyLimit(userId: string, channel: "email" | "phone", options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TDailyLimitResetResponse>>(() =>
      apiService.post(`${this.base}/users/${userId}/reset-daily-limit?channel=${channel}`, undefined, options)
    );
  }

  // Legal Hold
  setLegalHold(recordId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TLegalHoldResponse>>(() =>
      apiService.post(`${this.base}/records/${recordId}/legal-hold`, undefined, options)
    );
  }

  releaseLegalHold(recordId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TWrap<TLegalHoldResponse>>(() =>
      apiService.delete(`${this.base}/records/${recordId}/legal-hold`, undefined, options)
    );
  }
}

export const verificationAdminService = new VerificationAdminService();
